<template>
  <v-card class="rounded-lg">
    <v-row>
      <v-col>
        <v-card-title
          class="base text-subtitle-1 font-weight-bold rounded-t-lg"
        >
          {{ $t("sales.package_products") }}
        </v-card-title>
        <v-divider></v-divider>
      </v-col>
    </v-row>

    <v-card-text
      v-for="(prod, index) in $ticketPackage.reservedPackageContentPreview"
      :key="index"
    >
      <v-row class="pb-4">
        <v-col cols="3">
          <v-img
            :src="prod.img"
            alt="test"
            width="160"
            height="120"
            class="rounded"
          />
        </v-col>
        <v-col cols="7" sm="8" class="d-flex flex-column text-left px-0">
          <span class="text-subtitle-1 font-weight-bold">
            {{ prod.descricao }}</span
          >
          {{
            formatProductDate(prod.dthr_apresentacao) ||
            formatProductDate(prod.date)
          }}
          {{ prod.hour ? " - " + prod.hour : "" }}

          <template v-if="prod.quantityPossibilities.length > 0">
            <template
              v-for="(variation, indexVariation) in prod.quantityPossibilities"
            >
              <span
                v-if="variation.quantity > 0"
                :key="indexVariation"
                class="text-body-2"
              >
                {{ variation.quantity }} x
                {{ variation.descricao_completa || variation.descricao }} -
                {{ formatCurrency(variation.preco) }}
              </span>
            </template>
          </template>

          <template v-if="prod.tipo === 'CORTESIA'">
            Qtd:
            {{
              prod.quantityIfOnlyQuantity > 1
                ? ` ${prod.quantityIfOnlyQuantity} itens`
                : ` ${prod.quantityIfOnlyQuantity} item`
            }}
          </template>

          <template
            v-if="
              prod.quantityPossibilities.length > 0 &&
              prod.quantityPossibilities[0].quantity === 0 &&
              prod.quantityIfOnlyQuantity > 0 &&
              prod.tipo !== 'CORTESIA'
            "
          >
            <span class="text-body-2">
              {{ prod.quantityIfOnlyQuantity }} x
              {{
                formatCurrency(prod.total / prod.quantityIfOnlyQuantity) ||
                "R$ 0,00"
              }}
            </span>
          </template>

          <span
            class="text-body-2 font-weight-bold mt-2"
            v-if="prod.tipo !== 'CORTESIA'"
            >{{ $t("sales.subtotal") }}:
            {{ formatCurrency(prod.total) || "R$ 0,00" }}</span
          >
        </v-col>
        <v-col cols="2" sm="1" class="pa-0 pt-3">
          <v-btn
            icon
            @click="
              $ticketPackage.remove(
                prod,
                $t('sales.remove_product_confirmation')
              )
            "
          >
            <v-icon> mdi-delete-outline </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-divider></v-divider>
    </v-card-text>

    <v-btn
      :disabled="buttonDisabled || hasReachedLimit"
      text
      class="mr-4 text-caption mb-3"
      :class="hasAProductOnPackage ? 'mt-0' : 'mt-4'"
      plain
      @click="$ticketPackage.addProduct = true"
    >
      <v-icon small>{{ $config.icons.plus }}</v-icon>
      {{ $t("sales.add_product") }}
    </v-btn>

    <v-card-actions
      v-if="packageContentPreviewTotal && hasAProductOnPackage"
      class="base d-flex flex-column align-end pr-4"
    >
      <span class="text-body-1 font-weight-bold">
        {{ $t("sales.subtotal") }}: {{ subTotal }}
      </span>
      <span class="text-body-1 font-weight-bold" v-if="totalDiscount">
        {{ $t("sales.discount") }}: {{ totalDiscount }}
      </span>
      <span class="text-body-1 font-weight-bold">
        Total: {{ packageContentPreviewTotal }}
      </span>
    </v-card-actions>
  </v-card>
</template>

<script>
import { formatCurrency, formatProductDate } from "@/utils/productPackage.js";
import { mapMutations } from "vuex";

export default {
  name: "PackagePreview",
  computed: {
    isAdmin() {
      if (this.$axios.getLocal("oe_user_info")?.data?.user_type === "admin")
        return true;
      return false;
    },
    buttonDisabled() {
      const isVendaDireta = this.$ticketPackage.productTab;
      const taxaPacote =
        this.$axios.getLocal("loja_pacote")?.data?.aplica_taxa_pacote;
      const taxaCortesia =
        this.$axios.getLocal("loja_pacote")?.data?.aplica_taxa_cortesia;
      const taxaGratuidade =
        this.$axios.getLocal("loja_pacote")?.data?.aplica_taxa_gratuidade;
      const taxaPacoteLojista =
        this.$axios.getLocal("oe_user_info")?.data?.aplica_taxa_pacote;
      const taxaCortesiaLojista =
        this.$axios.getLocal("oe_user_info")?.data?.aplica_taxa_cortesia;
      const taxaGratuidadeLojista =
        this.$axios.getLocal("oe_user_info")?.data?.aplica_taxa_gratuidade;

      if (this.isAdmin) {
        if (isVendaDireta == 0 && taxaPacote == 0) {
          return true;
        }
        if (isVendaDireta == 1 && taxaCortesia == 0 && taxaGratuidade == 0) {
          return true;
        }
        return false;
      }
      if (!this.isAdmin) {
        if (isVendaDireta == 0 && taxaPacoteLojista == 0) {
          return true;
        }
        if (
          isVendaDireta == 1 &&
          taxaCortesiaLojista == 0 &&
          taxaGratuidadeLojista == 0
        ) {
          return true;
        }
        return false;
      }
      return false;
    },
    quantityPossibilities() {
      return this.$ticketPackage.reservedPackageContentPreview.flatMap(
        item => item.quantityPossibilities
      );
    },
    hasReachedLimit() {
      const totalTickets = this.quantityPossibilities.reduce((acc, item) => {
        return (acc += Number(item.quantity));
      }, 0);

      this.setTicketsInPackage(totalTickets);

      return totalTickets >= 50;
    },
    subTotal() {
      const totalPrice =
        this.$ticketPackage.reservedPackageContentPreview.reduce(
          (acc, item) => acc + item.total,
          0
        );

      return formatCurrency(totalPrice);
    },
    totalDiscount() {
      const totalDiscount =
        this.$ticketPackage.reservedPackageContentPreview.reduce(
          (acc, item) =>
            acc + (item.total * this.$ticketPackage.cadastro.desconto) / 100,
          0
        );

      return formatCurrency(totalDiscount);
    },
    packageContentPreviewTotal() {
      const totalPrice =
        this.$ticketPackage.reservedPackageContentPreview.reduce(
          (acc, item) =>
            acc +
            item.total -
            (item.total * this.$ticketPackage.cadastro.desconto) / 100,
          0
        );

      return formatCurrency(totalPrice);
    },
    hasAProductOnPackage() {
      return this.$ticketPackage.reservedPackageContentPreview.length > 0;
    },
  },
  methods: {
    ...mapMutations(["setTicketsInPackage"]),
  },
  data() {
    return {
      formatProductDate,
      formatCurrency,
    };
  },
};
</script>
